import FormControl from '../../../components-ui/spark/FormControl';
import FormHelperText from '../../../components-ui/spark/FormHelperText';
import FormLabel from '../../../components-ui/spark/FormLabel';
import Combobox from '../../../components-ui/spark/Combobox';
import AppDatePickerYMD from '../../../components-ui/date/AppDatePickerYMD';
import TextField from '../../../components-ui/spark/TextField';
import React from 'react';
import { Form, Formik } from 'formik';
import { Box, Stack, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import Button from '../../../components-ui/button';
import * as DotHocReducer from '../../../faetures/dot-hoc/dotHocSlide';
import { NotifyHelper } from '../../../helpers/toast';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { BSKT_DotHoc } from '../../../services/dot_hoc/DotHocModels';
import { dotHocService } from '../../../services/dot_hoc/dotHocService';
import MuiModal from '../components/mui-modal';
import { useHeDaoTao } from '../../../hooks/useHeDaoTao';

interface CreateOrEditDotHocModalProps {
    mode?: 'view' | 'edit';
    onClose: () => void;
}

const CreateOrEditDotHocModal = (props: CreateOrEditDotHocModalProps) => {
    const { onClose, mode = 'edit' } = props;
    const { createOrEditModel } = useAppSelector((x) => x.dotHoc);
    const dispatch = useDispatch();
    const [submitError, setSubmitError] = React.useState('');
    const systems = useHeDaoTao();

    const initValues: BSKT_DotHoc = {
        ID_dot_hoc: createOrEditModel.ID_dot_hoc,
        ID_he: createOrEditModel.ID_he ?? 0,
        Dot: createOrEditModel.Dot,
        Nam_tuyen_sinh: createOrEditModel.Nam_tuyen_sinh,
        Ten_dot: createOrEditModel.Ten_dot,
        Tu_ngay: createOrEditModel.Tu_ngay,
        Den_ngay: createOrEditModel.Den_ngay,
        Ghi_chu: createOrEditModel.Ghi_chu
    };

    const isEdit = !!createOrEditModel.ID_dot_hoc && createOrEditModel.ID_dot_hoc > 0;
    const isViewMode = mode === 'view';

    const handleSubmit = async (values: BSKT_DotHoc) => {
        setSubmitError('');
        if (values.ID_dot_hoc === 0) {
            const result = await dotHocService.create(values);
            if (!result.is_success) {
                setSubmitError(result.message || 'Không thêm được đợt học.');
                NotifyHelper.Error(result.message || 'Lỗi khi thêm mới đợt học');
                return false;
            }

            dispatch(DotHocReducer.insertSuccess(result.data ?? values));
            NotifyHelper.Success(`Đợt học “${(values.Ten_dot ?? '').trim()}” đã được tạo.`, 'Thêm mới thành công');
            return true;
        }

        const result = await dotHocService.update(values);
        if (!result.is_success) {
            setSubmitError(result.message || 'Không cập nhật được đợt học.');
            NotifyHelper.Error(result.message || 'Lỗi khi cập nhật đợt học');
            return false;
        }

        dispatch(DotHocReducer.updateSuccess(result.data ?? values));
        NotifyHelper.Success(`Đợt học “${(values.Ten_dot ?? '').trim()}” đã được lưu.`, 'Cập nhật thành công');
        return true;
    };

    return (
        <MuiModal
            isOpen={true}
            onClose={onClose}
            title={isViewMode ? 'Xem chi tiết đợt học' : isEdit ? 'Cập nhật đợt học' : 'Thêm mới đợt học'}
        >
            <Formik
                initialValues={initValues}
                enableReinitialize
                validateOnChange={true}
                validateOnBlur={true}
                validate={(values) => {
                    const errors: Partial<Record<keyof BSKT_DotHoc, string>> = {};
                    if (!String(values.Ten_dot ?? '').trim()) errors.Ten_dot = 'Tên đợt học là bắt buộc';
                    if (!systems.items.some(item=>item.ID_he===Number(values.ID_he))) errors.ID_he = 'Vui lòng chọn hệ hợp lệ từ danh mục';
                    if (!Number(values.Nam_tuyen_sinh || 0)) errors.Nam_tuyen_sinh = 'Năm tuyển sinh là bắt buộc';
                    if (!String(values.Tu_ngay ?? '').trim()) errors.Tu_ngay = 'Từ ngày là bắt buộc';
                    if (!String(values.Den_ngay ?? '').trim()) errors.Den_ngay = 'Đến ngày là bắt buộc';
                    if (values.Tu_ngay && values.Den_ngay && String(values.Den_ngay).slice(0,10) < String(values.Tu_ngay).slice(0,10)) errors.Den_ngay = 'Đến ngày phải từ ngày bắt đầu trở đi';
                    return errors;
                }}
                onSubmit={async (values, helper) => {
                    if (isViewMode) {
                        helper.setSubmitting(false);
                        return;
                    }

                    try {
                        if (await handleSubmit(values)) { helper.resetForm(); onClose(); }
                    } catch {
                        setSubmitError('Không lưu được đợt học. Vui lòng thử lại.');
                        NotifyHelper.Error('Không lưu được đợt học. Vui lòng thử lại.');
                    } finally { helper.setSubmitting(false); }
                }}
            >
                {({ values, handleChange, errors, touched, isSubmitting, resetForm, setSubmitting, setFieldValue, setFieldTouched }) => (
                    <Form>
                        {systems.error && <div className="alert alert-danger" role="alert">{systems.error}</div>}
                        {!systems.loading && !systems.error && values.ID_he>0 && !systems.items.some(item=>item.ID_he===Number(values.ID_he)) && <div className="alert alert-warning" role="alert">Đợt học đang lưu mã hệ không thuộc danh mục sau đại học. Vui lòng chọn lại hệ chính xác trước khi lưu.</div>}
                        {submitError && <div className="alert alert-danger" role="alert">{submitError}</div>}
                        <Box mb={2}>
                            <Typography variant="body2" color="text.secondary">
                                Nhập thông tin đợt học, hệ đào tạo, năm tuyển sinh và thời gian áp dụng.
                            </Typography>
                        </Box>

                        <Box className="app-form-card__section app-form-grid">
                            <FormControl fullWidth size="small" className="app-form-grid__full">
                                <FormLabel>
                                    Tên đợt học <span className="text-danger">*</span>
                                </FormLabel>
                                <TextField
                                    value={values.Ten_dot}
                                    name="Ten_dot"
                                    onChange={handleChange}
                                    placeholder="Tên đợt học"
                                    size="small"
                                    disabled={isViewMode}
                                />
                                {touched.Ten_dot && errors.Ten_dot && <FormHelperText error>{errors.Ten_dot}</FormHelperText>}
                            </FormControl>

                            <div className="period-form-identifiers app-form-grid__full">
                            <FormControl fullWidth size="small">
                                <FormLabel>
                                    Hệ <span className="text-danger">*</span>
                                </FormLabel>
                                <Combobox
                                    label="Hệ đào tạo"
                                    value={String(values.ID_he ?? 0)}
                                    placeholder="Chọn hệ đào tạo"
                                    options={systems.items.map(item=>({value:String(item.ID_he),label:item.Ten_he}))}
                                    onChange={value=>{void setFieldValue('ID_he',Number(value));void setFieldTouched('ID_he',true,false);}}
                                    disabled={isViewMode||systems.loading||!!systems.error}
                                />
                                {touched.ID_he && errors.ID_he && <FormHelperText error>{errors.ID_he}</FormHelperText>}
                            </FormControl>

                            <FormControl fullWidth size="small">
                                <FormLabel>Đợt</FormLabel>
                                <TextField
                                    placeholder="Số thứ tự đợt"
                                    type="number"
                                    value={values.Dot}
                                    name="Dot"
                                    onChange={handleChange}
                                    size="small"
                                    disabled={isViewMode}
                                />
                                {touched.Dot && errors.Dot && <FormHelperText error>{errors.Dot}</FormHelperText>}
                            </FormControl>

                            <FormControl fullWidth size="small">
                                <FormLabel>
                                    Năm tuyển sinh <span className="text-danger">*</span>
                                </FormLabel>
                                <TextField
                                    placeholder="Năm tuyển sinh"
                                    value={values.Nam_tuyen_sinh}
                                    name="Nam_tuyen_sinh"
                                    onChange={handleChange}
                                    size="small"
                                    disabled={isViewMode}
                                />
                                {touched.Nam_tuyen_sinh && errors.Nam_tuyen_sinh && (
                                    <FormHelperText error>{errors.Nam_tuyen_sinh}</FormHelperText>
                                )}
                            </FormControl>
                            </div>

                            <FormControl fullWidth size="small">
                                <FormLabel>
                                    Từ ngày <span className="text-danger">*</span>
                                </FormLabel>
                                <AppDatePickerYMD
                                    value={values.Tu_ngay ? values.Tu_ngay.toString().slice(0, 10) : ''}
                                    onChange={value=>{void setFieldValue('Tu_ngay',value);void setFieldTouched('Tu_ngay',true,false);}}
                                    disabled={isViewMode}
                                />
                                {touched.Tu_ngay && errors.Tu_ngay && <FormHelperText error>{errors.Tu_ngay}</FormHelperText>}
                            </FormControl>

                            <FormControl fullWidth size="small">
                                <FormLabel>
                                    Đến ngày <span className="text-danger">*</span>
                                </FormLabel>
                                <AppDatePickerYMD
                                    value={values.Den_ngay ? values.Den_ngay.toString().slice(0, 10) : ''}
                                    onChange={value=>{void setFieldValue('Den_ngay',value);void setFieldTouched('Den_ngay',true,false);}}
                                    minDate={values.Tu_ngay || undefined}
                                    disabled={isViewMode}
                                />
                                {touched.Den_ngay && errors.Den_ngay && <FormHelperText error>{errors.Den_ngay}</FormHelperText>}
                            </FormControl>

                            <FormControl fullWidth size="small" className="app-form-grid__full">
                                <FormLabel>Ghi chú</FormLabel>
                                <TextField
                                    multiline
                                    rows={3}
                                    placeholder="Ghi chú"
                                    value={values.Ghi_chu}
                                    name="Ghi_chu"
                                    onChange={handleChange}
                                    size="small"
                                    disabled={isViewMode}
                                />
                            </FormControl>
                        </Box>

                        <Box mt={2}>
                            <Stack direction="row" className="app-form-actions" spacing={2}>
                                <Typography variant="caption" color="text.secondary">
                                    <span style={{ color: '#e53935' }}>*</span> Trường bắt buộc nhập
                                </Typography>

                                <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                                    {!isViewMode && (
                                        <Button variant="contained" type="submit" size="small" isLoading={isSubmitting} disabled={systems.loading||!!systems.error||!systems.items.length}>
                                            {isEdit ? 'Cập nhật' : 'Lưu'}
                                        </Button>
                                    )}
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        onClick={() => {
                                            resetForm();
                                            setSubmitting(false);
                                            onClose();
                                        }}
                                    >
                                        Đóng
                                    </Button>
                                </Box>
                            </Stack>
                        </Box>
                    </Form>
                )}
            </Formik>
        </MuiModal>
    );
};

export default CreateOrEditDotHocModal;
